// ---------------------------------------------------------------------------
// live-effects.ts -- AUTHOR-facing types + helpers for writing a Bounded live
// module that emits EFFECTS (call an LLM for an AI NPC, run a function, settle a
// transaction, write authoritative data) from its otherwise-pure tick.
//
// The tick stays a PURE, synchronous reducer: it cannot call out. Instead it
// EMITS effects as data alongside the next state; the platform runs them off the
// deterministic loop and re-injects each result as a recorded `@effect` intent on
// a later tick — so replay stays bit-identical (it reads the recorded result and
// never re-executes/re-charges). See BOUNDED-LIVE-EFFECTS-SPEC.md §3/§9.
//
// SOURCE OF TRUTH for these contracts is the runtime core
// (realtime-worker/src/effects-core.ts). This module mirrors the AUTHOR-facing
// subset for the SDK (the two are kept structurally identical by the shared spec).
// ---------------------------------------------------------------------------
/** Reserved intent address an effect result arrives on. */
export const EFFECT_INTENT_ADDRESS = "@effect";
// ---------------------------------------------------------------------------
// Ergonomic helpers
// ---------------------------------------------------------------------------
/**
 * Build the `{ state, effects }` tick return. Always include this (even with an
 * empty list) when you intend the wrapper, so the runtime never has to guess
 * whether your state object happens to look like the wrapper.
 *
 *   return withEffects(next, [{ kind: "ai", id: `npc:${id}:${next.turn}`, model, input }]);
 */
export function withEffects(state, effects = []) {
    return { state, effects };
}
/**
 * Narrow an incoming intent to an effect result inside your tick:
 *
 *   for (const i of intents) {
 *     if (i.address === EFFECT_INTENT_ADDRESS && isEffectResult(i.intent)) {
 *       applyResult(state, i.intent); // i.intent.effectId / .ok / .result / .error
 *     } else { applyPlayerIntent(state, i); }
 *   }
 */
export function isEffectResult(intent) {
    return (!!intent &&
        typeof intent === "object" &&
        intent.__effect === true &&
        typeof intent.effectId === "string" &&
        typeof intent.ok === "boolean");
}
/** Identity helper purely for type inference when authoring a module. */
export function defineLiveModule(mod) {
    return mod;
}
